/**
 * QMS Followers Hooks
 * TanStack Query mutations for adding/removing ticket followers and self follow/unfollow.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { addQmsFollower, removeQmsFollower, followSelf, unfollowSelf } from '../api/qmsApi';
import { qmsKeys } from './useQmsBoard';

/**
 * Mutation hook to add a specific follower (by userId) to a QMS ticket.
 * On success, invalidates the ticket query so the followers list refreshes.
 */
export function useAddQmsFollower(ticketId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => addQmsFollower(ticketId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to add follower';
      toast.error('Failed to add follower', { description: message });
    },
  });
}

/**
 * Mutation hook to remove a specific follower (by userId) from a QMS ticket.
 * On success, invalidates the ticket query so the followers list refreshes.
 */
export function useRemoveQmsFollower(ticketId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeQmsFollower(ticketId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to remove follower';
      toast.error('Failed to remove follower', { description: message });
    },
  });
}

/**
 * Mutation hook for the current viewer to follow a ticket.
 * Backend resolves identity from the session cookie; no userId param.
 */
export function useFollowSelf(ticketId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => followSelf(ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to follow ticket';
      toast.error('Failed to follow ticket', { description: message });
    },
  });
}

/**
 * Mutation hook for the current viewer to unfollow a ticket.
 * Backend resolves identity from the session cookie; no userId param.
 */
export function useUnfollowSelf(ticketId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => unfollowSelf(ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to unfollow ticket';
      toast.error('Failed to unfollow ticket', { description: message });
    },
  });
}
